import React, { Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ActiveRoleBoundary from "@/components/auth/ActiveRoleBoundary";
import { hasAssignedRole } from "@/utils/activeRole";
import { PermissionProvider } from "@/context/PermissionContext";

// Eager: Layouts & Login (needed immediately)
// Fix 49B REVERTED (Feb 3, 2026) — Lazy-loading layouts terbukti bikin app
// lebih lambat karena Suspense fallback jitter + roundtrip chunk. Kembalikan
// ke eager import. Preconnect Cloudinary di index.html tetap dipertahankan.
import LoginPage from "@/pages/LoginPage";
import AdminLayout from "@/layouts/AdminLayout";
import MarketingLayout from "@/layouts/MarketingLayout";
import NakesLayout from "@/layouts/NakesLayout";
import GudangLayout from "@/layouts/GudangLayout";
import FinanceLayout from "@/layouts/FinanceLayout";
import RequireFinanceScope from "@/components/RequireFinanceScope";
import AnnouncementPopup from "@/components/AnnouncementPopup";
import AppUpdatesPopup from "@/components/AppUpdatesPopup";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Lazy import with one-shot retry on chunk-load failure (handles stale cached chunks
// after a hot reload — e.g. "Cannot read properties of undefined (reading 'call')").
const lazyWithRetry = (importFn) =>
  React.lazy(async () => {
    try {
      return await importFn();
    } catch (err) {
      const key = "lazy-chunk-reload";
      if (typeof window !== "undefined" && window.sessionStorage.getItem(key) !== "1") {
        window.sessionStorage.setItem(key, "1");
        window.location.reload();
        // Sep 2026 — Stub while reload is in flight: was `() => null` (fully
        // blank) — on a slow/flaky connection (nakes/marketing in the field)
        // the reload can take a while, so this rendered as a plain white
        // screen with no feedback. PageLoader is a plain inline component
        // (no icon lib, defined in this same file, eagerly bundled) — safe
        // to use here since it can never itself fail to load.
        return { default: () => <PageLoader message="Memuat ulang, mohon tunggu..." /> };
      }
      // Sep 2026 — Second attempt also failed (e.g. the reload above also
      // landed on a stale cached index.html via the Service Worker's
      // navigate-fallback, so the same chunk 404s again). Tag before
      // re-throwing so the global ErrorBoundary (src/components/ErrorBoundary.js)
      // can show the specific "app updated, please reload" message instead
      // of a generic error — reliable regardless of the exact browser-specific
      // chunk-error wording.
      const tagged = err instanceof Error ? err : new Error(String(err));
      tagged.isChunkLoadRetryFailure = true;
      throw tagged;
    }
  });

// Lazy-loaded page components (code-split per route)
const AdminDashboard = lazyWithRetry(() => import("@/pages/admin/AdminDashboard"));
const AdminTasks = lazyWithRetry(() => import("@/pages/admin/AdminTasks"));
const PapanOperasional = lazyWithRetry(() => import("@/pages/admin/PapanOperasional"));
const PapanOperasionalThread = lazyWithRetry(() => import("@/pages/admin/PapanOperasionalThread"));
const MasterEntities = lazyWithRetry(() => import("@/pages/admin/MasterEntities"));
const AdminNotifications = lazyWithRetry(() => import("@/pages/admin/AdminNotifications"));
const TelegramAdmin = lazyWithRetry(() => import("@/pages/admin/TelegramAdmin"));
const NotificationTestLab = lazyWithRetry(() => import("@/pages/admin/NotificationTestLab"));
const UsersPage = lazyWithRetry(() => import("@/pages/admin/UsersPage"));
const MarketingTeamsPage = lazyWithRetry(() => import("@/pages/admin/MarketingTeamsPage"));
const AdminProfile = lazyWithRetry(() => import("@/pages/admin/Profile"));
const AdminSupplyRequests = lazyWithRetry(() => import("@/pages/admin/SupplyRequests"));
const AdminSupplyRequestDetail = lazyWithRetry(() => import("@/pages/admin/SupplyRequestDetail"));
const AdminNakesHistory = lazyWithRetry(() => import("@/pages/admin/NakesHistory"));
const NursesList = lazyWithRetry(() => import("@/pages/admin/NursesList"));
const NurseImport = lazyWithRetry(() => import("@/pages/admin/NurseImport"));
const AdminOrdersList = lazyWithRetry(() => import("@/pages/admin/OrdersList"));
const OrderDetail = lazyWithRetry(() => import("@/pages/admin/OrderDetail"));
const WorkflowDetail = lazyWithRetry(() => import("@/pages/admin/WorkflowDetail"));
const EditNurse = lazyWithRetry(() => import("@/pages/admin/EditNurse"));
const AdminReports = lazyWithRetry(() => import("@/pages/admin/Reports"));
const AdminDiscussions = lazyWithRetry(() => import("@/pages/admin/Discussions"));
const AdminDiscussionRoom = lazyWithRetry(() => import("@/pages/admin/DiscussionRoom"));
const AdminJobDiscussions = lazyWithRetry(() => import("@/pages/admin/JobDiscussions"));
const AdminNakesChats = lazyWithRetry(() => import("@/pages/admin/NakesChats"));
const AdminNakesChatRoom = lazyWithRetry(() => import("@/pages/admin/NakesChatRoom"));
const OrderNakesChats = lazyWithRetry(() => import("@/pages/admin/OrderNakesChats"));
const AdminScheduleTable = lazyWithRetry(() => import("@/pages/admin/ScheduleTable"));
const MasterProducts = lazyWithRetry(() => import("@/pages/admin/MasterProducts"));
const MasterInventory = lazyWithRetry(() => import("@/pages/admin/MasterInventory"));
const GlobalEquipmentUsage = lazyWithRetry(() => import("@/pages/admin/GlobalEquipmentUsage"));
const MedicalRecords = lazyWithRetry(() => import("@/pages/admin/MedicalRecords"));
const MedicalRecordDetail = lazyWithRetry(() => import("@/pages/admin/MedicalRecordDetail"));
const MedicalDocuments = lazyWithRetry(() => import("@/pages/admin/MedicalDocuments"));
const RMEVerification = lazyWithRetry(() => import("@/pages/admin/RMEVerification"));
const PatientTimeline = lazyWithRetry(() => import("@/pages/admin/PatientTimeline"));
const EncounterView = lazyWithRetry(() => import("@/pages/admin/EncounterView"));
const CompareEncounters = lazyWithRetry(() => import("@/pages/admin/CompareEncounters"));
const PublicSummary = lazyWithRetry(() => import("@/pages/public/PublicSummary"));
const MasterSupplier = lazyWithRetry(() => import("@/pages/admin/MasterSupplier"));
const MasterVaxaProduct = lazyWithRetry(() => import("@/pages/admin/MasterVaxaProduct"));
const ProductConfirmations = lazyWithRetry(() => import("@/pages/shared/ProductConfirmations"));
const MasterSupplierProduct = lazyWithRetry(() => import("@/pages/admin/MasterSupplierProduct"));
const PurchaseOrders = lazyWithRetry(() => import("@/pages/admin/PurchaseOrders"));
const PurchaseOrderDetail = lazyWithRetry(() => import("@/pages/admin/PurchaseOrderDetail"));
const Invoices = lazyWithRetry(() => import("@/pages/admin/Invoices"));
const InvoiceForm = lazyWithRetry(() => import("@/pages/admin/InvoiceForm"));
const InvoiceDetail = lazyWithRetry(() => import("@/pages/admin/InvoiceDetail"));
const InvoiceViewer = lazyWithRetry(() => import("@/pages/shared/InvoiceViewer"));
const POPDFViewer = lazyWithRetry(() => import("@/pages/shared/POPDFViewer"));
const SuratTugasViewer = lazyWithRetry(() => import("@/pages/shared/SuratTugasViewer"));
const SuratPengantarViewer = lazyWithRetry(() => import("@/pages/admin/SuratPengantarViewer"));
const SuratSakitViewer = lazyWithRetry(() => import("@/pages/shared/SuratSakitViewer"));
const CustomerVoucherPage = lazyWithRetry(() => import("@/pages/CustomerVoucherPage"));
const PublicVoucherPage = lazyWithRetry(() => import("@/pages/PublicVoucherPage"));
const VoucherList = lazyWithRetry(() => import("@/pages/admin/VoucherList"));
const VoucherDetail = lazyWithRetry(() => import("@/pages/admin/VoucherDetail"));
const CustomerVoucherDetail = lazyWithRetry(() => import("@/pages/admin/CustomerVoucherDetail"));
const VoucherImport = lazyWithRetry(() => import("@/pages/admin/VoucherImport"));
const CustomerMasterList = lazyWithRetry(() => import("@/pages/admin/CustomerMasterList"));
const ProgramBlast = lazyWithRetry(() => import("@/pages/admin/ProgramBlast"));
const BlastVoucher = lazyWithRetry(() => import("@/pages/admin/BlastVoucher"));
const DaftarBlock = lazyWithRetry(() => import("@/pages/admin/DaftarBlock"));
const PromoInfo = lazyWithRetry(() => import("@/pages/marketing/PromoInfo"));
const CustomerMasterDetail = lazyWithRetry(() => import("@/pages/admin/CustomerMasterDetail"));
const Customer360 = lazyWithRetry(() => import("@/pages/admin/Customer360"));
const CustomerServiceHistory = lazyWithRetry(() => import("@/pages/admin/CustomerServiceHistory"));
const CustomerCredits = lazyWithRetry(() => import("@/pages/admin/CustomerCredits"));
const IncompleteWorkflowsList = lazyWithRetry(() => import("@/pages/admin/IncompleteWorkflowsList"));
const LegacyCustomerImport = lazyWithRetry(() => import("@/pages/admin/LegacyCustomerImport"));
const VoucherTemplates = lazyWithRetry(() => import("@/pages/admin/VoucherTemplates"));
const VoucherTermsSettings = lazyWithRetry(() => import("@/pages/admin/VoucherTermsSettings"));
const VoucherExtendHistory = lazyWithRetry(() => import("@/pages/admin/VoucherExtendHistory"));
const ReferrersManagement = lazyWithRetry(() => import("@/pages/admin/ReferrersManagement"));
const ReferralDashboard = lazyWithRetry(() => import("@/pages/public/ReferralDashboard"));
const PublicVaccineRecord = lazyWithRetry(() => import("@/pages/public/PublicVaccineRecord"));
const ScreeningForm = lazyWithRetry(() => import("@/pages/public/ScreeningForm"));
const PublicOrderScreening = lazyWithRetry(() => import("@/pages/public/PublicOrderScreening"));
const PublicBookingForm = lazyWithRetry(() => import("@/pages/public/PublicBookingForm"));
const MarketingDailyReport = lazyWithRetry(() => import("@/pages/admin/MarketingDailyReport"));
const NakesDirectory = lazyWithRetry(() => import("@/pages/admin/NakesDirectory"));
const NakesDocumentsPage = lazyWithRetry(() => import("@/pages/admin/NakesDocumentsPage"));
const MarketingProfile = lazyWithRetry(() => import("@/pages/marketing/Profile"));
const MarketingOrdersList = lazyWithRetry(() => import("@/pages/marketing/OrdersList"));
const MarketingReports = lazyWithRetry(() => import("@/pages/marketing/Reports"));
const MarketingDiscussions = lazyWithRetry(() => import("@/pages/marketing/Discussions"));
const MarketingDiscussionRoom = lazyWithRetry(() => import("@/pages/marketing/DiscussionRoom"));
const MarketingJobDiscussions = lazyWithRetry(() => import("@/pages/marketing/JobDiscussions"));
const MarketingScheduleTable = lazyWithRetry(() => import("@/pages/marketing/ScheduleTable"));
const MarketingTasks = lazyWithRetry(() => import("@/pages/marketing/MarketingTasks"));
const MarketingDashboard = lazyWithRetry(() => import("@/pages/marketing/MarketingDashboard"));
const MarketingNotifications = lazyWithRetry(() => import("@/pages/marketing/MarketingNotifications"));
const CreateOrder = lazyWithRetry(() => import("@/pages/marketing/CreateOrder"));
const EditOrder = lazyWithRetry(() => import("@/pages/marketing/EditOrder"));
const VaccineDueList = lazyWithRetry(() => import("@/pages/marketing/VaccineDueList"));
const VaccineDataList = lazyWithRetry(() => import("@/pages/admin/VaccineDataList"));
const VaccineScheduleMaster = lazyWithRetry(() => import("@/pages/admin/VaccineScheduleMaster"));
const MarketingReminderDebug = lazyWithRetry(() => import("@/pages/admin/MarketingReminderDebug"));
const AdminReminderDebug = lazyWithRetry(() => import("@/pages/admin/AdminReminderDebug"));
const NakesReminderDebug = lazyWithRetry(() => import("@/pages/admin/NakesReminderDebug"));
const TelegramMonitor = lazyWithRetry(() => import("@/pages/admin/TelegramMonitor"));
const DirektoriNakes = lazyWithRetry(() => import("@/pages/admin/DirektoriNakes"));
const NakesProfile = lazyWithRetry(() => import("@/pages/nakes/Profile"));
const WilayahLayanan = lazyWithRetry(() => import("@/pages/nakes/WilayahLayanan"));
const NakesAssignments = lazyWithRetry(() => import("@/pages/nakes/Assignments"));
const NakesJobs = lazyWithRetry(() => import("@/pages/nakes/Jobs"));
const NakesDashboard = lazyWithRetry(() => import("@/pages/nakes/NakesDashboard"));
const NakesSchedule = lazyWithRetry(() => import("@/pages/nakes/NakesSchedule"));
const NakesJobDetail = lazyWithRetry(() => import("@/pages/nakes/JobDetail"));
const WorkflowJobDetail = lazyWithRetry(() => import("@/pages/nakes/WorkflowJobDetail"));
const StockUsage = lazyWithRetry(() => import("@/pages/nakes/StockUsage"));
const ServiceConfirmation = lazyWithRetry(() => import("@/pages/nakes/ServiceConfirmation"));
const UploadIC = lazyWithRetry(() => import("@/pages/nakes/UploadICNew"));
const UploadSelfie = lazyWithRetry(() => import("@/pages/nakes/UploadSelfie"));
const UploadSampah = lazyWithRetry(() => import("@/pages/nakes/UploadSampah"));
const UploadDokumentasi = lazyWithRetry(() => import("@/pages/nakes/UploadDokumentasi"));
const MyUsageSummary = lazyWithRetry(() => import("@/pages/nakes/MyUsageSummary"));
const PrivilegeHistory = lazyWithRetry(() => import("@/pages/nakes/PrivilegeHistory"));
const NakesChats = lazyWithRetry(() => import("@/pages/nakes/Chats"));
const NakesChatRoom = lazyWithRetry(() => import("@/pages/nakes/ChatRoom"));
const NakesJobDiscussions = lazyWithRetry(() => import("@/pages/nakes/JobDiscussions"));
const NakesSharedJobs = lazyWithRetry(() => import("@/pages/nakes/SharedJobs"));
const NotificationsPage = lazyWithRetry(() => import("@/pages/nakes/NotificationsPage"));
const SupplyRequests = lazyWithRetry(() => import("@/pages/nakes/SupplyRequests"));
const NakesSupplyRequestDetail = lazyWithRetry(() => import("@/pages/nakes/SupplyRequestDetail"));
const SupplyHistory = lazyWithRetry(() => import("@/pages/nakes/SupplyHistory"));
const NakesScorecard = lazyWithRetry(() => import("@/pages/nakes/NakesScorecard"));
const PDFViewer = lazyWithRetry(() => import("@/pages/shared/PDFViewer"));
const GudangProfile = lazyWithRetry(() => import("@/pages/gudang/Profile"));
const GudangDashboard = lazyWithRetry(() => import("@/pages/gudang/GudangDashboard"));
const GudangChat = lazyWithRetry(() => import("@/pages/gudang/GudangChat"));
const GudangSupplyRequests = lazyWithRetry(() => import("@/pages/gudang/SupplyRequests"));
const GudangSupplyRequestDetail = lazyWithRetry(() => import("@/pages/gudang/SupplyRequestDetail"));
const GudangMasterSupplier = lazyWithRetry(() => import("@/pages/gudang/MasterSupplier"));
const GudangMasterVaxaProduct = lazyWithRetry(() => import("@/pages/gudang/MasterVaxaProduct"));
const GudangMasterSupplierProduct = lazyWithRetry(() => import("@/pages/gudang/MasterSupplierProduct"));
const GudangPurchaseOrders = lazyWithRetry(() => import("@/pages/gudang/PurchaseOrders"));
const GudangPurchaseOrderDetail = lazyWithRetry(() => import("@/pages/gudang/PurchaseOrderDetail"));
const NakesHistoryGudang = lazyWithRetry(() => import("@/pages/gudang/NakesHistory"));
const ChatNakes = lazyWithRetry(() => import("@/pages/gudang/ChatNakes"));
const ChatNakesDetail = lazyWithRetry(() => import("@/pages/gudang/ChatNakesDetail"));
const GudangNotifications = lazyWithRetry(() => import("@/pages/gudang/GudangNotifications"));
const JobDiscussionRoom = lazyWithRetry(() => import("@/pages/shared/JobDiscussionRoom"));
const ChatGudangNakes = lazyWithRetry(() => import("@/pages/admin/ChatGudangNakes"));
const ChatGudangNakesDetail = lazyWithRetry(() => import("@/pages/admin/ChatGudangNakesDetail"));
const AllChats = lazyWithRetry(() => import("@/pages/admin/AllChats"));
const CoordinationChannels = lazyWithRetry(() => import("@/pages/shared/CoordinationChannels"));
const AdminLogisticReturns = lazyWithRetry(() => import("@/pages/admin/LogisticReturns"));
const AdminLogisticReturnDetail = lazyWithRetry(() => import("@/pages/admin/LogisticReturnDetail"));
const AdminCreateLogisticReturn = lazyWithRetry(() => import("@/pages/admin/CreateLogisticReturn"));
const GudangLogisticReturns = lazyWithRetry(() => import("@/pages/gudang/LogisticReturns"));
const GudangLogisticReturnDetail = lazyWithRetry(() => import("@/pages/gudang/LogisticReturnDetail"));
const GudangCreateLogisticReturn = lazyWithRetry(() => import("@/pages/gudang/CreateLogisticReturn"));
const NakesLogisticReturns = lazyWithRetry(() => import("@/pages/nakes/LogisticReturns"));
const NakesLogisticReturnDetail = lazyWithRetry(() => import("@/pages/nakes/LogisticReturnDetail"));
const NakesCreateLogisticReturn = lazyWithRetry(() => import("@/pages/nakes/CreateLogisticReturn"));
const NakesAnnouncements = lazyWithRetry(() => import("@/pages/nakes/NakesAnnouncements"));
const NakesAnnouncementDetail = lazyWithRetry(() => import("@/pages/nakes/NakesAnnouncementDetail"));
const NakesSOP = lazyWithRetry(() => import("@/pages/nakes/NakesSOP"));
const NakesSOPDetail = lazyWithRetry(() => import("@/pages/nakes/NakesSOPDetail"));
const GudangAnnouncements = lazyWithRetry(() => import("@/pages/gudang/GudangAnnouncements"));
const GudangKomplainNakes = lazyWithRetry(() => import("@/pages/gudang/KomplainNakes"));
const GudangAnnouncementDetail = lazyWithRetry(() => import("@/pages/gudang/GudangAnnouncementDetail"));
const AuditLog = lazyWithRetry(() => import("@/pages/admin/AuditLog"));
const ActiveUsers = lazyWithRetry(() => import("@/pages/admin/ActiveUsers"));
const DocumentSettings = lazyWithRetry(() => import("@/pages/admin/DocumentSettings"));
const PaymentSettings = lazyWithRetry(() => import("@/pages/admin/PaymentSettings"));
const BankAccountSettings = lazyWithRetry(() => import("@/pages/admin/BankAccountSettings"));
const EntitySettings = lazyWithRetry(() => import("@/pages/admin/EntitySettings"));
const RegionSettings = lazyWithRetry(() => import("@/pages/admin/RegionSettings"));
const MessageTemplates = lazyWithRetry(() => import("@/pages/admin/MessageTemplates"));
const OrderTrash = lazyWithRetry(() => import("@/pages/admin/OrderTrash"));
const AnnouncementsPage = lazyWithRetry(() => import("@/pages/admin/Announcements"));
const RoleManagement = lazyWithRetry(() => import("@/pages/admin/RoleManagement"));
const ModuleGroupManagement = lazyWithRetry(() => import("@/pages/admin/ModuleGroupManagement"));
const KnowledgeCenter = lazyWithRetry(() => import("@/pages/admin/KnowledgeCenter"));
const AnnouncementForm = lazyWithRetry(() => import("@/pages/admin/AnnouncementForm"));
const AnnouncementDetail = lazyWithRetry(() => import("@/pages/admin/AnnouncementDetail"));
const SOPDocumentDetail = lazyWithRetry(() => import("@/pages/admin/SOPDocumentDetail"));
const SOPDocumentForm = lazyWithRetry(() => import("@/pages/admin/SOPDocumentForm"));
const ClinicalSOPDetail = lazyWithRetry(() => import("@/pages/admin/ClinicalSOPDetail"));
const ClinicalSOPForm = lazyWithRetry(() => import("@/pages/admin/ClinicalSOPForm"));
const ServiceSOPDetail = lazyWithRetry(() => import("@/pages/admin/ServiceSOPDetail"));
const ServiceSOPListPage = lazyWithRetry(() => import("@/pages/shared/ServiceSOPListPage"));
const NakesGuidelinesPage = lazyWithRetry(() => import("@/pages/shared/NakesGuidelinesPage"));
const NakesGuidelineDetail = lazyWithRetry(() => import("@/pages/shared/NakesGuidelineDetail"));
const TutorialPage = lazyWithRetry(() => import("@/pages/shared/TutorialPage"));
const TutorialCMS = lazyWithRetry(() => import("@/pages/admin/TutorialCMS"));
const AppUpdatesPage = lazyWithRetry(() => import("@/pages/shared/AppUpdatesPage"));
const HelpCenterPage = lazyWithRetry(() => import("@/pages/shared/HelpCenterPage"));
const MarketingHelpPage = lazyWithRetry(() => import("@/pages/marketing/MarketingHelpPage"));
const AppUpdatesManage = lazyWithRetry(() => import("@/pages/admin/AppUpdatesManage"));
const HelpdeskKnowledgeBase = lazyWithRetry(() => import("@/pages/admin/HelpdeskKnowledgeBase"));
const MedicineDictionary = lazyWithRetry(() => import("@/pages/admin/MedicineDictionary"));
const AdminStokOpnameList = lazyWithRetry(() => import("@/pages/admin/StokOpname").then(m => ({ default: m.AdminStokOpnameListPage })));
const AdminStokOpnameDetail = lazyWithRetry(() => import("@/pages/admin/StokOpname").then(m => ({ default: m.AdminStokOpnameDetailPage })));
const NakesStokOpnamePage = lazyWithRetry(() => import("@/pages/nakes/NakesStokOpnamePage"));
const PayrollList = lazyWithRetry(() => import("@/pages/admin/PayrollList"));
const PayrollDetail = lazyWithRetry(() => import("@/pages/admin/PayrollDetail"));
const PayrollNakesDetail = lazyWithRetry(() => import("@/pages/admin/PayrollNakesDetail"));
const PayrollGlobalReport = lazyWithRetry(() => import("@/pages/admin/PayrollGlobalReport"));
const PayrollPphReport = lazyWithRetry(() => import("@/pages/admin/PayrollPphReport"));
const AdminAttendance = lazyWithRetry(() => import("@/pages/admin/AdminAttendance"));
const PayrollSettings = lazyWithRetry(() => import("@/pages/admin/PayrollSettings"));
const ExtraTransportHistory = lazyWithRetry(() => import("@/pages/admin/ExtraTransportHistory"));
const DepositList = lazyWithRetry(() => import("@/pages/admin/DepositList"));
const DepositDetail = lazyWithRetry(() => import("@/pages/admin/DepositDetail"));
const ProductCategoriesPage = lazyWithRetry(() => import("@/pages/admin/ProductCategoriesPage"));
const NakesAttendancePage = lazyWithRetry(() => import("@/pages/nakes/NakesAttendancePage"));
const HelpdeskTickets = lazyWithRetry(() => import("@/pages/admin/HelpdeskTickets"));
const KnowledgeBase = lazyWithRetry(() => import("@/pages/admin/KnowledgeBase"));
const PrivilegeRequests = lazyWithRetry(() => import("@/pages/admin/PrivilegeRequests"));
const TeleconsultationAdmin = lazyWithRetry(() => import("@/pages/admin/TeleconsultationAdmin"));
const TeleconsultationDetail = lazyWithRetry(() => import("@/pages/admin/TeleconsultationDetail"));
const DoctorConsultation = lazyWithRetry(() => import("@/pages/admin/DoctorConsultation"));
const PatientConsultation = lazyWithRetry(() => import("@/pages/public/PatientConsultation"));
const FinanceDashboard = lazyWithRetry(() => import("@/pages/finance/FinanceDashboard"));
const InvoicesNoProof = lazyWithRetry(() => import("@/pages/finance/InvoicesNoProof"));
const FinanceChatMarketing = lazyWithRetry(() => import("@/pages/finance/FinanceChatMarketing"));
const FinanceChatMarketingThread = lazyWithRetry(() => import("@/pages/finance/FinanceChatMarketingThread"));
const HRDScorecardDashboard = lazyWithRetry(() => import("@/pages/admin/HRDScorecardDashboard"));
const NakesClassPage = lazyWithRetry(() => import("@/pages/admin/NakesClassPage"));
const ScorecardMonthly = lazyWithRetry(() => import("@/pages/admin/ScorecardMonthly"));
const VerifikasiSOPNakes = lazyWithRetry(() => import("@/pages/admin/VerifikasiSOPNakes"));
const VerifikasiSOPDetail = lazyWithRetry(() => import("@/pages/admin/VerifikasiSOPDetail"));
const SOPAuditList = lazyWithRetry(() => import("@/pages/admin/SOPAuditList"));
const SOPAuditReport = lazyWithRetry(() => import("@/pages/admin/SOPAuditReport"));
const CekBarangKeluar = lazyWithRetry(() => import("@/pages/admin/CekBarangKeluar"));
const AdminHandlingJobs = lazyWithRetry(() => import("@/pages/admin/AdminHandlingJobs"));
const FreeOrderReport = lazyWithRetry(() => import("@/pages/admin/FreeOrderReport"));
const PancakeReport = lazyWithRetry(() => import("@/pages/admin/PancakeReport"));
const MidtransPayments = lazyWithRetry(() => import("@/pages/admin/MidtransPayments"));
const ComplaintManagement = lazyWithRetry(() => import("@/pages/admin/ComplaintManagement"));
const NakesWarningLetters = lazyWithRetry(() => import("@/pages/nakes/WarningLetters"));
const StockMonitorGudang = lazyWithRetry(() => import("@/pages/shared/StockMonitorGudang"));
const StockMonitorNakes = lazyWithRetry(() => import("@/pages/shared/StockMonitorNakes"));
const NakesStockMonitorBlocked = lazyWithRetry(() => import("@/pages/nakes/StockMonitorBlocked"));
const KartuStok = lazyWithRetry(() => import("@/pages/shared/KartuStok"));
const CustomerPaymentPage = lazyWithRetry(() => import("@/pages/customer/CustomerPaymentPage"));
const CustomerPaymentResultPage = lazyWithRetry(() => import("@/pages/customer/CustomerPaymentPage").then(m => ({ default: m.CustomerPaymentResultPage })));
const ReceiptPublicPage = lazyWithRetry(() => import("@/pages/public/ReceiptPublicPage"));

import { usePresenceTracking } from "@/hooks/usePresenceTracking";
import { Toaster } from "@/components/ui/sonner";
import PdfViewerModal from "@/components/shared/PdfViewerModal";
import ChatToastNotifier from "@/components/ChatToastNotifier";
import RequirePermission from "@/components/RequirePermission";
import ConnectivityLayer from "@/components/ConnectivityLayer";

// Suspense fallback for lazy-loaded pages
const PageLoader = ({ message = "Memuat halaman..." }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      <p className="text-sm text-gray-500">{message}</p>
    </div>
  </div>
);

// ProtectedRoute now checks assigned_roles for super_admin, not hardcoded user_id
const ProtectedRoute = ({ children, allowedRoles, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check Super Admin requirement - based on assigned_roles OR role, not hardcoded user_id
  if (requireSuperAdmin) {
    const hasSuperAdminRole = (user.assigned_roles || []).includes('super_admin') || 
                              ['super_admin', 'master_admin'].includes(user.role);
    if (!hasSuperAdminRole) {
      // Show 403 page for non-super-admin trying to access restricted routes
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Akses Ditolak</h1>
            <p className="text-gray-600 mb-4">
              Halaman ini hanya dapat diakses oleh Super Admin.
            </p>
            <button 
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Kembali
            </button>
          </div>
        </div>
      );
    }
  }

  // Expand allowedRoles to include super_admin, master_admin, and supervisor
  // when "admin" is allowed
  let expandedAllowedRoles = allowedRoles ? [...allowedRoles] : [];
  if (expandedAllowedRoles.includes("admin")) {
    if (!expandedAllowedRoles.includes("super_admin")) expandedAllowedRoles.push("super_admin");
    if (!expandedAllowedRoles.includes("master_admin")) expandedAllowedRoles.push("master_admin");
    if (!expandedAllowedRoles.includes("supervisor")) expandedAllowedRoles.push("supervisor");
  }

  // Okt 2026 — Role tambahan Marketing: akun yang dicentang Marketing (mis. Finance yang merangkap)
  // boleh membuka halaman /marketing/*. Hanya untuk jalur itu; guard lain tidak berubah.
  const viaExtraMarketing =
    location.pathname.startsWith("/marketing") &&
    expandedAllowedRoles.includes("marketing") &&
    hasAssignedRole(user, "marketing");

  if (expandedAllowedRoles.length > 0 && !expandedAllowedRoles.includes(user.role) && !viaExtraMarketing) {
    // FIX: Redirect finance users to their appropriate page
    if (user.role === "finance") {
      return <Navigate to="/finance/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// Legacy redirect helper: /admin/hrd/sop-audit/:orderId/:nakesUserId
// → /admin/nakes-performance/verifikasi-sop/:orderId/:nakesUserId
function LegacySOPAuditRedirect() {
  const { orderId, nakesUserId } = useParams();
  return <Navigate to={`/admin/nakes-performance/verifikasi-sop/${orderId}/${nakesUserId}`} replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Presence tracking - sends heartbeat when user is authenticated
  usePresenceTracking(!!user);

  // Fix 47 / Fix 53 — Handle notif click from service worker (postMessage).
  // Saat user tap PWA push notification, sw.js focus window ini lalu kirim
  // {type:'notif-navigate', url}. Kita route via react-router supaya tidak
  // full-reload halaman. Console log ada untuk memudahkan debug regression.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined;
    const onMessage = (event) => {
      const data = event.data || {};
      if (data.type !== 'notif-navigate' || !data.url) return;
      console.log('[App notif-navigate] received url=', data.url);
      try {
        const u = new URL(data.url, window.location.origin);
        if (u.origin === window.location.origin) {
          const path = u.pathname + u.search + u.hash;
          console.log('[App notif-navigate] react-router navigate ->', path);
          navigate(path);
        } else {
          console.log('[App notif-navigate] cross-origin, hard nav ->', data.url);
          window.location.href = data.url;
        }
      } catch (_e) {
        window.location.href = data.url;
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [navigate]);

  // FIX: Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
    {/* Login Announcement Popup — mounted globally; only shows for authed users */}
    <AnnouncementPopup user={user} />
    {/* Okt 2026 — Popup "Apa yang Baru": sekali per update per user */}
    <AppUpdatesPopup user={user} />
    {/* Feb 2026 — Impersonate banner (super_admin/spv only, hides when inactive) */}
    <ImpersonationBanner />
    <Routes>
      {/* Public Routes */}
      <Route path="/voucher/:token" element={<CustomerVoucherPage />} />
      <Route path="/v/:code" element={<PublicVoucherPage />} />
      
      <Route
        path="/"
        element={
          user ? (
            <Navigate
              to={
                ["admin", "super_admin", "master_admin", "supervisor", "hrd"].includes(user.role)
                  ? "/admin/dashboard"
                  : user.role === "finance"
                  ? "/finance/dashboard"
                  : user.role === "marketing"
                  ? "/marketing/orders"
                  : user.role === "nakes"
                  ? "/nakes/jobs"
                  : "/gudang/dashboard"
              }
              replace
            />
          ) : (
            <LoginPage />
          )
        }
      />
      
      {/* Login route */}
      <Route path="/login" element={<LoginPage />} />

      {/* Admin Routes */}
      {/* Fix 51 — Finance role DIHAPUS dari outer /admin/* allowedRoles.
          Sebelumnya finance bisa masuk AdminLayout dan sidebar tercampur.
          Sekarang finance auto-redirect ke /finance/dashboard via
          ProtectedRoute (line 324-326). FIN001/FIN002 punya scope terpisah
          (RequireFinanceScope) di /finance/* routes. Marketing & HRD tetap
          dipertahankan karena mereka memang berbagi AdminLayout secara sah. */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={["admin", "marketing", "hrd"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<AdminDashboard />} />
        
        {/* Admin/HRD/SPV Multi-Tab Tasks */}
        <Route path="tasks" element={
          <ProtectedRoute allowedRoles={["admin", "hrd"]}>
            <AdminTasks />
          </ProtectedRoute>
        } />

        {/* Papan Operasional - Admin/SuperAdmin/HRD */}
        <Route path="papan-operasional" element={
          <ProtectedRoute allowedRoles={["admin", "hrd"]}>
            <PapanOperasional />
          </ProtectedRoute>
        } />
        <Route path="papan-operasional/:poId" element={
          <ProtectedRoute allowedRoles={["admin", "hrd"]}>
            <PapanOperasionalThread />
          </ProtectedRoute>
        } />

        {/* Master Entitas - Super Admin only */}
        <Route path="master-entities" element={
          <ProtectedRoute allowedRoles={["admin"]} requireSuperAdmin>
            <MasterEntities />
          </ProtectedRoute>
        } />
        
        {/* Notifications - accessible by admin and finance */}
        <Route path="notifications" element={
          <ProtectedRoute allowedRoles={["admin", "finance"]}>
            <AdminNotifications />
          </ProtectedRoute>
        } />
        
        {/* Telegram Admin - admin only */}
        <Route path="telegram" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TelegramAdmin />
          </ProtectedRoute>
        } />

        {/* Notification Test Lab — Super Admin / Master Admin / Supervisor only */}
        <Route path="system/notifications" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor"]}>
            <NotificationTestLab />
          </ProtectedRoute>
        } />
        
        {/* Finance-restricted routes - admin only */}
        <Route path="users" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="marketing-teams" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor"]}>
            <MarketingTeamsPage />
          </ProtectedRoute>
        } />
        <Route path="nurses" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <NursesList />
          </ProtectedRoute>
        } />
        <Route path="nurses/import" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <NurseImport />
          </ProtectedRoute>
        } />
        <Route path="nurses/:userId/edit" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <EditNurse />
          </ProtectedRoute>
        } />
        <Route path="orders" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminOrdersList />
          </ProtectedRoute>
        } />
        <Route path="orders/new" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CreateOrder />
          </ProtectedRoute>
        } />
        <Route path="orders/:orderId" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <OrderDetail />
          </ProtectedRoute>
        } />
        <Route path="orders/:orderId/workflow" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <WorkflowDetail />
          </ProtectedRoute>
        } />
        <Route path="orders/:orderId/nakes-chats" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <OrderNakesChats />
          </ProtectedRoute>
        } />
        <Route path="orders/:orderId/edit" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <EditOrder />
          </ProtectedRoute>
        } />
        <Route path="reports" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminReports />
          </ProtectedRoute>
        } />
        
        {/* P0 - HRD Scorecard Dashboard */}
        <Route path="hrd/scorecards" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "hrd"]}>
            <HRDScorecardDashboard />
          </ProtectedRoute>
        } />
        
        {/* NEW: Scorecard Monthly Aggregation */}
        <Route path="hrd/scorecard-monthly" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin"]}>
            <ScorecardMonthly />
          </ProtectedRoute>
        } />
        
        {/* NEW: Nakes Performance Routes */}
        <Route path="nakes-performance/scorecards" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "hrd"]}>
            <HRDScorecardDashboard />
          </ProtectedRoute>
        } />
        
        {/* Scorecard Monthly via Nakes Performance menu */}
        <Route path="nakes-performance/scorecard-monthly" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin"]}>
            <ScorecardMonthly />
          </ProtectedRoute>
        } />

        {/* Kelas Nakes (Dynamic Class System) — Super Admin + Supervisor only */}
        <Route path="nakes-performance/kelas-nakes" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor"]}>
            <NakesClassPage />
          </ProtectedRoute>
        } />
        
        {/* Marketing Daily Report (KPI Grade) */}
        <Route path="reports/marketing-daily" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "owner", "marketing"]}>
            <MarketingDailyReport />
          </ProtectedRoute>
        } />
        
        {/* Nakes Directory (Admin only - View Only) */}
        <Route path="nakes-directory" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "owner"]}>
            <NakesDirectory />
          </ProtectedRoute>
        } />
        <Route path="nakes-documents/:nakesId" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin"]}>
            <NakesDocumentsPage />
          </ProtectedRoute>
        } />
        
        <Route path="nakes-performance/verifikasi-sop" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "head_admin"]}>
            <VerifikasiSOPNakes />
          </ProtectedRoute>
        } />
        
        <Route path="nakes-performance/verifikasi-sop/:orderId/:nakesUserId" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "head_admin"]}>
            <VerifikasiSOPDetail />
          </ProtectedRoute>
        } />

        {/* Okt 2026 — Audit buta Verifikasi SOP (SPV dan Super Admin saja) */}
        <Route path="nakes-performance/sop-audit" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor"]}>
            <SOPAuditList />
          </ProtectedRoute>
        } />

        <Route path="nakes-performance/cek-barang-keluar" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor"]}>
            <CekBarangKeluar />
          </ProtectedRoute>
        } />

        <Route path="nakes-performance/sop-audit-report" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor"]}>
            <SOPAuditReport />
          </ProtectedRoute>
        } />

        <Route path="nakes-performance/sop-audit/:orderId/:nakesUserId" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor"]}>
            <VerifikasiSOPDetail mode="audit" />
          </ProtectedRoute>
        } />

        {/* Okt 2026 — Laporan Order Gratis (Super Admin saja; server juga menolak role lain) */}
        <Route path="free-orders-report" element={
          <ProtectedRoute requireSuperAdmin>
            <FreeOrderReport />
          </ProtectedRoute>
        } />

        {/* Okt 2026 — Pembayaran Midtrans (Super Admin, SPV, Admin CRM; server juga menolak role lain) */}
        <Route path="midtrans-payments" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin", "supervisor", "admin"]}>
            <MidtransPayments />
          </ProtectedRoute>
        } />

        {/* Okt 2026 — Laporan Chat Marketing dari Pancake (Super Admin saja; server juga menolak role lain) */}
        <Route path="pancake-report" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <PancakeReport />
          </ProtectedRoute>
        } />

        {/* Handling Job Admin (Feb 2026) — claim/release per admin + progress tracking */}
        <Route path="handling-jobs" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "supervisor"]}>
            <AdminHandlingJobs />
          </ProtectedRoute>
        } />
        
        {/* P1 - Complaint & Warning Letter System */}
        <Route path="nakes-performance/complaints" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "hrd", "marketing"]}>
            <ComplaintManagement />
          </ProtectedRoute>
        } />
        
        {/* Legacy routes - redirect to new paths */}
        <Route path="hrd/sop-audit" element={<Navigate to="/admin/nakes-performance/verifikasi-sop" replace />} />
        <Route path="hrd/sop-audit/:orderId/:nakesUserId" element={<LegacySOPAuditRedirect />} />
        <Route path="hrd/complaints" element={<Navigate to="/admin/nakes-performance/complaints" replace />} />
        
        <Route path="discussions" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDiscussions />
          </ProtectedRoute>
        } />
        <Route path="discussions/:discussionId" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDiscussionRoom />
          </ProtectedRoute>
        } />
        <Route path="job-discussions" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminJobDiscussions />
          </ProtectedRoute>
        } />
        <Route path="job-discussions/:orderId/:nakesId" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <JobDiscussionRoom />
          </ProtectedRoute>
        } />
        {/* NEW: Group discussion route (1 channel per order, no nakesId) */}
        <Route path="job-discussions/:orderId" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <JobDiscussionRoom />
          </ProtectedRoute>
        } />
        <Route path="nakes-chats" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <AdminNakesChats />
          </ProtectedRoute>
        } />
        <Route path="nakes-chats/:chatId" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <AdminNakesChatRoom />
          </ProtectedRoute>
        } />
        <Route path="all-chats" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AllChats />
          </ProtectedRoute>
        } />
        <Route path="coordination-channels" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CoordinationChannels />
          </ProtectedRoute>
        } />
        <Route path="chat-gudang-nakes" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ChatGudangNakes />
          </ProtectedRoute>
        } />
        <Route path="chat-gudang-nakes/:nakesId" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ChatGudangNakesDetail />
          </ProtectedRoute>
        } />
        <Route path="schedule" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminScheduleTable />
          </ProtectedRoute>
        } />
        {/* Feb 2026 — Alias Indonesian route pointing to same AdminScheduleTable,
            allowed roles diperluas untuk marketing juga (sesuai request). */}
        <Route path="jadwal" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <AdminScheduleTable />
          </ProtectedRoute>
        } />
        <Route path="master-products" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <MasterProducts />
          </ProtectedRoute>
        } />
        <Route path="medical-records" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <MedicalRecords />
          </ProtectedRoute>
        } />
        <Route path="medical-records/:recordId" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <MedicalRecordDetail />
          </ProtectedRoute>
        } />
        <Route path="medical-documents" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <MedicalDocuments />
          </ProtectedRoute>
        } />
        <Route path="rme/:rmeId" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <RMEVerification />
          </ProtectedRoute>
        } />
        <Route path="patients/:patientId/timeline" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <PatientTimeline />
          </ProtectedRoute>
        } />
        <Route path="patients/:patientId/compare" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <CompareEncounters />
          </ProtectedRoute>
        } />
        <Route path="encounters/:encounterId" element={
          <ProtectedRoute allowedRoles={["admin", "marketing"]}>
            <EncounterView />
          </ProtectedRoute>
        } />
        <Route path="master-inventory" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <MasterInventory />
          </ProtectedRoute>
        } />
        
        {/* Stock Monitor — Gudang Pusat (admin/super_admin/gudang access) */}
        <Route path="stock-monitor/gudang" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "gudang"]}>
            <StockMonitorGudang />
          </ProtectedRoute>
        } />

        {/* Stock Monitor — Nakes (admin/super_admin/gudang access; nakes use /nakes route) */}
        <Route path="stock-monitor/nakes" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "gudang"]}>
            <StockMonitorNakes />
          </ProtectedRoute>
        } />

        {/* Kartu Stok (Nov 2026) — Gudang Pusat dan nakes, di grup Manajemen Logistik */}
        <Route path="kartu-stok" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "supervisor", "gudang"]}>
            <KartuStok />
          </ProtectedRoute>
        } />
        {/* Alamat lama tetap jalan */}
        <Route path="kartu-stok-nakes" element={<Navigate to="/admin/kartu-stok" replace />} />
        <Route path="kartu-stok-gudang" element={<Navigate to="/admin/kartu-stok" replace />} />

        {/* Master Data Routes for Purchase Order */}
        <Route path="master-supplier" element={
          <ProtectedRoute allowedRoles={["admin", "gudang"]}>
            <MasterSupplier />
          </ProtectedRoute>
        } />
        <Route path="master-vaxa-product" element={
          <ProtectedRoute allowedRoles={["admin", "gudang"]}>
            <MasterVaxaProduct />
          </ProtectedRoute>
        } />
        <Route path="konfirmasi-produk" element={
          <ProtectedRoute allowedRoles={["admin", "gudang"]}>
            <ProductConfirmations />
          </ProtectedRoute>
        } />
        <Route path="master-supplier-product" element={
          <ProtectedRoute allowedRoles={["admin", "gudang"]}>
            <MasterSupplierProduct />
          </ProtectedRoute>
        } />
        
        {/* Purchase Order Routes - Finance can view for payment management */}
        <Route path="purchase-orders" element={
          <ProtectedRoute allowedRoles={["admin", "gudang", "finance"]}>
            <PurchaseOrders />
          </ProtectedRoute>
        } />
        <Route path="purchase-orders/:poId" element={
          <ProtectedRoute allowedRoles={["admin", "gudang", "finance"]}>
            <PurchaseOrderDetail />
          </ProtectedRoute>
        } />
        
        {/* Okt 2026 — Menu Keuangan di sidebar Admin: hanya untuk akun yang dicentang finance.console */}
        <Route path="finance/dashboard" element={<RequirePermission permission="finance.console"><FinanceDashboard /></RequirePermission>} />
        <Route path="finance/no-proof" element={<RequirePermission permission="finance.console"><InvoicesNoProof /></RequirePermission>} />

        {/* Invoice routes - accessible by both admin and finance */}
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/new/:orderId" element={<InvoiceForm />} />
        <Route path="invoices/:invoiceId" element={<InvoiceDetail />} />
        <Route path="invoices/:invoiceId/edit" element={<InvoiceForm />} />
        
        {/* Voucher routes - accessible by admin, finance, and marketing */}
        <Route path="vouchers" element={<VoucherList />} />
        <Route path="vouchers/:voucherId" element={<VoucherDetail />} />
        <Route path="vouchers/customer/:phone" element={<CustomerVoucherDetail />} />
        <Route path="vouchers/import" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing"]}>
            <VoucherImport />
          </ProtectedRoute>
        } />
        <Route path="vouchers/templates" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <VoucherTemplates />
          </ProtectedRoute>
        } />
        <Route path="vouchers/terms" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <VoucherTermsSettings />
          </ProtectedRoute>
        } />
        <Route path="vouchers/extend-history" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing", "finance"]}>
            <VoucherExtendHistory />
          </ProtectedRoute>
        } />
        
        {/* Customer Master Data */}
        <Route path="customers/master" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing", "finance"]}>
            <CustomerMasterList />
          </ProtectedRoute>
        } />
        <Route path="promo-programs" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "supervisor"]}>
            <ProgramBlast />
          </ProtectedRoute>
        } />
        <Route path="blast-voucher" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "supervisor"]}>
            <BlastVoucher />
          </ProtectedRoute>
        } />
        <Route path="daftar-block" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "supervisor"]}>
            <DaftarBlock />
          </ProtectedRoute>
        } />
        <Route path="customers/master/:customerId" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing", "finance"]}>
            <CustomerMasterDetail />
          </ProtectedRoute>
        } />
        <Route path="customers/360/:customerId" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing"]}>
            <Customer360 />
          </ProtectedRoute>
        } />
        <Route path="customers/360/:customerId/riwayat-layanan" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing"]}>
            <CustomerServiceHistory />
          </ProtectedRoute>
        } />
        <Route path="customers/credits" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "supervisor", "finance"]}>
            <CustomerCredits />
          </ProtectedRoute>
        } />
        <Route path="incomplete-workflows" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "supervisor"]}>
            <IncompleteWorkflowsList />
          </ProtectedRoute>
        } />
        {/* Route customers/360/:customerId/rme removed - using /admin/medical-records?customer_id=X instead */}
        
        {/* LIST DATA VAKSIN - Admin & Marketing can access (P0 Feature) */}
        <Route path="vaccine-data-list" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing"]}>
            <VaccineDataList />
          </ProtectedRoute>
        } />
        
        {/* Legacy Vaccine Due List - Redirect to new page */}
        <Route path="vaccine-due-list" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing"]}>
            <VaccineDataList />
          </ProtectedRoute>
        } />
        
        {/* Master Jadwal Vaksin - Admin only */}
        <Route path="vaccine-schedule-master" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <VaccineScheduleMaster />
          </ProtectedRoute>
        } />
        
        {/* Marketing Reminder Debug Console - Super Admin only */}
        <Route path="marketing-reminder-debug" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <MarketingReminderDebug />
          </ProtectedRoute>
        } />
        
        {/* Admin Reminder Debug Console - Super Admin only */}
        <Route path="admin-reminder-debug" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <AdminReminderDebug />
          </ProtectedRoute>
        } />
        
        {/* Nakes Reminder Debug Console - Super Admin only */}
        <Route path="nakes-reminder-debug" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <NakesReminderDebug />
          </ProtectedRoute>
        } />

        {/* Unified Telegram Monitor - Super Admin only */}
        <Route path="telegram-monitor" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <TelegramMonitor />
          </ProtectedRoute>
        } />

        {/* Direktori Nakes by Area - Admin/Super Admin (canEditWilayah=true) */}
        <Route path="direktori-nakes" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <DirektoriNakes canEditWilayah={true} />
          </ProtectedRoute>
        } />

        {/* Knowledge Base AI - Super Admin only */}
        <Route path="knowledge-base" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]}>
            <KnowledgeBase />
          </ProtectedRoute>
        } />
        
        <Route path="customers/legacy-import" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <LegacyCustomerImport />
          </ProtectedRoute>
        } />
        
        {/* Teleconsultation routes */}
        <Route path="teleconsult" element={<TeleconsultationAdmin />} />
        <Route path="teleconsult/:consultationId" element={<DoctorConsultation />} />
        <Route path="teleconsult/:consultationId/detail" element={<TeleconsultationDetail />} />
        
        {/* Referrer Management - Admin only */}
        <Route path="referrers" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <ReferrersManagement />
          </ProtectedRoute>
        } />
        
        <Route path="supply-requests" element={<AdminSupplyRequests />} />
        <Route path="supply-requests/:requestId" element={<AdminSupplyRequestDetail />} />
        <Route path="nakes-history/:nakesId" element={<AdminNakesHistory />} />
        
        {/* Global Equipment Usage - accessible by admin/marketing */}
        <Route path="equipment-usage" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "marketing"]}>
            <GlobalEquipmentUsage />
          </ProtectedRoute>
        } />
        
        {/* Logistic Returns - accessible by admin */}
        <Route path="logistic-returns" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminLogisticReturns />
          </ProtectedRoute>
        } />
        <Route path="logistic-returns/create" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminCreateLogisticReturn />
          </ProtectedRoute>
        } />
        <Route path="logistic-returns/:returnId" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminLogisticReturnDetail />
          </ProtectedRoute>
        } />
        
        {/* Audit Log & Active Users - SUPER ADMIN ONLY */}
        <Route path="audit-log" element={
          <ProtectedRoute allowedRoles={["admin"]} requireSuperAdmin={true}>
            <AuditLog />
          </ProtectedRoute>
        } />
        <Route path="active-users" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ActiveUsers />
          </ProtectedRoute>
        } />
        
        {/* Document Settings - admin only */}
        <Route path="document-settings" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <DocumentSettings />
          </ProtectedRoute>
        } />

        {/* Payment Method Settings — Super Admin only (toggles Midtrans methods) */}
        <Route path="payment-settings" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]} requireSuperAdmin>
            <PaymentSettings />
          </ProtectedRoute>
        } />

        {/* Bank Account Settings — Super Admin only (CRUD rekening perusahaan) */}
        <Route path="bank-accounts" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]} requireSuperAdmin>
            <BankAccountSettings />
          </ProtectedRoute>
        } />

        {/* Entity Settings — Super Admin only (CRUD entitas) */}
        <Route path="entity-settings" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]} requireSuperAdmin>
            <EntitySettings />
          </ProtectedRoute>
        } />

        {/* Entity Settings — Super Admin only (CRUD entitas) */}
        <Route path="entity-settings" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]} requireSuperAdmin>
            <EntitySettings />
          </ProtectedRoute>
        } />

        {/* Region Settings — Super Admin only (master wilayah for region-based job sharing) */}
        <Route path="region-settings" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]} requireSuperAdmin>
            <RegionSettings />
          </ProtectedRoute>
        } />

        {/* Tempat Sampah Order (Sep 2026) — Super Admin only */}
        <Route path="order-trash" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]} requireSuperAdmin>
            <OrderTrash />
          </ProtectedRoute>
        } />

        {/* Message Templates Management — Super Admin only */}
        <Route path="message-templates" element={
          <ProtectedRoute allowedRoles={["super_admin", "master_admin"]} requireSuperAdmin>
            <MessageTemplates />
          </ProtectedRoute>
        } />

        {/* Announcement Banners — Super Admin + Supervisor */}
        <Route path="announcements" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "master_admin", "supervisor"]}>
            <AnnouncementsPage />
          </ProtectedRoute>
        } />
        
        {/* Privilege Requests - admin only */}
        <Route path="privilege-requests" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <PrivilegeRequests />
          </ProtectedRoute>
        } />
        
        {/* RBAC Management - Super Admin (ADM001) only */}
        <Route path="roles" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <RoleManagement />
          </ProtectedRoute>
        } />
        <Route path="module-groups" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ModuleGroupManagement />
          </ProtectedRoute>
        } />
        
        {/* Knowledge Center (Pusat Informasi) - accessible by admin and finance */}
        <Route path="knowledge-center" element={<KnowledgeCenter />} />
        <Route path="knowledge-center/announcements/new" element={<AnnouncementForm />} />
        <Route path="knowledge-center/announcements/:announcementId" element={<AnnouncementDetail />} />
        <Route path="knowledge-center/announcements/:announcementId/edit" element={<AnnouncementForm />} />
        {/* SOP Aturan Routes */}
        <Route path="knowledge-center/sop-documents/new" element={<SOPDocumentForm />} />
        <Route path="knowledge-center/sop-documents/:sopId" element={<SOPDocumentDetail />} />
        <Route path="knowledge-center/sop-documents/:sopId/edit" element={<SOPDocumentForm />} />
        {/* SOP Tindakan Routes */}
        <Route path="knowledge-center/clinical-sops/new" element={<ClinicalSOPForm />} />
        <Route path="knowledge-center/clinical-sops/:sopId" element={<ClinicalSOPDetail />} />
        <Route path="knowledge-center/clinical-sops/:sopId/edit" element={<ClinicalSOPForm />} />
        {/* SOP Layanan Routes (iter372) */}
        <Route path="knowledge-center/service-sops/:sopId" element={<ServiceSOPDetail />} />
        {/* Panduan & Aturan Nakes (iter376) — Admin can view all types */}
        <Route path="guidelines" element={<NakesGuidelinesPage />} />
        <Route path="guidelines/:guidelineId" element={<NakesGuidelineDetail />} />
        
        {/* Tutorial - accessible by all roles */}
        <Route path="tutorial" element={<TutorialPage />} />
        <Route path="apa-yang-baru" element={<AppUpdatesPage />} />

        {/* Tutorial CMS & Knowledge Base — Super Admin / Supervisor */}
        <Route path="settings/tutorial-cms" element={<TutorialCMS />} />
        <Route path="settings/apa-yang-baru" element={<AppUpdatesManage />} />
        <Route path="settings/helpdesk-kb" element={<HelpdeskKnowledgeBase />} />
        <Route path="settings/medicine-dictionary" element={<MedicineDictionary />} />
        <Route path="stok-opname" element={<AdminStokOpnameList />} />
        <Route path="stok-opname/:id" element={<AdminStokOpnameDetail />} />

        {/* Payroll Nakes - Sprint 2 Batch C */}
        <Route path="payroll" element={<PayrollList />} />
        <Route path="payroll/report" element={<PayrollGlobalReport />} />
        <Route path="payroll/pph-report" element={<PayrollPphReport />} />
        <Route path="attendance" element={<AdminAttendance />} />
        <Route path="payroll/settings" element={<PayrollSettings />} />
        <Route path="payroll/runs/:id" element={<PayrollDetail />} />
        <Route path="payroll/runs/:id/nakes/:nakesId" element={<PayrollNakesDetail />} />

        {/* Extra Transport & Nego Transport History — Iter178 (May 2026)
            Accessible by Admin / SPV / Super Admin. */}
        <Route path="nakes/extra-transport-history" element={<ExtraTransportHistory />} />

        {/* Deposit Nakes — Feb 2026 (SPV + Super Admin only) */}
        <Route path="deposits" element={<DepositList />} />
        <Route path="deposits/:userId" element={<DepositDetail />} />

        {/* Product Categories — Feb 2026 (SPV + Super Admin only) */}
        <Route path="product-categories" element={<ProductCategoriesPage />} />
        
        {/* Helpdesk Tickets - Admin only */}
        <Route path="helpdesk/tickets" element={<HelpdeskTickets />} />
        
        {/* Profile - accessible by all */}
        <Route path="profile" element={<AdminProfile />} />
      </Route>

      {/* Marketing Routes */}
      <Route
        path="/marketing/*"
        element={
          <ProtectedRoute allowedRoles={["marketing"]}>
            <ActiveRoleBoundary role="marketing">
              <MarketingLayout />
            </ActiveRoleBoundary>
          </ProtectedRoute>
        }
      >
        {/* Dashboard - Control Tower */}
        <Route path="dashboard" element={<MarketingDashboard />} />
        
        {/* Tasks - Legacy route, redirect to dashboard */}
        <Route path="tasks" element={<MarketingDashboard />} />
        
        <Route path="orders" element={<MarketingOrdersList />} />
        <Route path="orders/new" element={<CreateOrder />} />
        <Route path="orders/:orderId" element={<OrderDetail />} />
        <Route path="orders/:orderId/workflow" element={<WorkflowDetail />} />
        <Route path="orders/:orderId/nakes-chats" element={<OrderNakesChats />} />
        <Route path="orders/:orderId/edit" element={<EditOrder />} />
        
        {/* FIX: Add Invoice Routes for Marketing */}
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/new/:orderId" element={<InvoiceForm />} />
        <Route path="invoices/:invoiceId" element={<InvoiceDetail />} />
        <Route path="invoices/:invoiceId/edit" element={<InvoiceForm />} />
        
        {/* Voucher routes for Marketing */}
        <Route path="vouchers" element={<VoucherList />} />
        <Route path="vouchers/:voucherId" element={<VoucherDetail />} />
        <Route path="vouchers/customer/:phone" element={<CustomerVoucherDetail />} />
        
        {/* Customer Master Data for Marketing */}
        <Route path="customers/master" element={<CustomerMasterList />} />
        <Route path="customers/master/:customerId" element={<CustomerMasterDetail />} />
        <Route path="promo-info" element={<PromoInfo />} />
        <Route path="daftar-block" element={<DaftarBlock />} />
        <Route path="customers/360/:customerId" element={<Customer360 />} />
        <Route path="customers/360/:customerId/riwayat-layanan" element={<CustomerServiceHistory />} />
        
        {/* LIST DATA VAKSIN for Marketing (P0 Feature) */}
        <Route path="vaccine-data-list" element={<VaccineDataList />} />
        <Route path="vaccine-due-list" element={<VaccineDataList />} />
        
        <Route path="reports" element={<MarketingReports />} />
        <Route path="reports/daily" element={<MarketingDailyReport />} />
        <Route path="all-chats" element={<AllChats />} />
        <Route path="nakes-chats" element={<AdminNakesChats />} />
        <Route path="nakes-chats/:chatId" element={<AdminNakesChatRoom />} />
        <Route path="discussions" element={<MarketingDiscussions />} />
        <Route path="discussions/:discussionId" element={<MarketingDiscussionRoom />} />
        <Route path="job-discussions" element={<MarketingJobDiscussions />} />
        <Route path="job-discussions/:orderId/:nakesId" element={<JobDiscussionRoom />} />
        {/* NEW: Group discussion route (1 channel per order, no nakesId) */}
        <Route path="job-discussions/:orderId" element={<JobDiscussionRoom />} />
        <Route path="coordination-channels" element={<CoordinationChannels />} />
        <Route path="coordination-channels/:channelType" element={<CoordinationChannels />} />
        {/* Fix 57 Bug B — Marketing chat dgn Finance. Reuse FinanceChatMarketing
            components (role-aware: detect marketing user → list finance users). */}
        <Route path="chats/finance" element={<FinanceChatMarketing />} />
        <Route path="chats/finance/:userId" element={<FinanceChatMarketingThread />} />
        <Route path="schedule" element={<MarketingScheduleTable />} />

        {/* Direktori Nakes by Area - Marketing (canEditWilayah=false) */}
        <Route path="direktori-nakes" element={<DirektoriNakes canEditWilayah={false} />} />
        
        {/* Pusat Informasi - Marketing-specific routes (stay in MarketingLayout) */}
        <Route path="knowledge-center" element={<KnowledgeCenter />} />
        <Route path="knowledge-center/announcements/:announcementId" element={<AnnouncementDetail />} />
        <Route path="knowledge-center/clinical-sops/:sopId" element={<ClinicalSOPDetail />} />
        <Route path="knowledge-center/sop-documents/:sopId" element={<SOPDocumentDetail />} />
        <Route path="knowledge-center/service-sops/:sopId" element={<ServiceSOPDetail />} />
        <Route path="tutorial" element={<Navigate to="/marketing/bantuan" replace />} />
        <Route path="bantuan" element={<MarketingHelpPage />} />
        <Route path="apa-yang-baru" element={<AppUpdatesPage />} />
        
        <Route path="profile" element={<MarketingProfile />} />
        
        {/* P1 - Complaint & Warning Letter (Marketing read + create complaints) */}
        <Route path="nakes-performance/complaints" element={<ComplaintManagement />} />
        
        {/* Notifications */}
        <Route path="notifications" element={<MarketingNotifications />} />
        
        {/* Papan Operasional — shared with admin/HRD/gudang/finance */}
        <Route path="papan-operasional" element={<PapanOperasional />} />
        <Route path="papan-operasional/:poId" element={<PapanOperasionalThread />} />
      </Route>

      {/* Nakes Routes */}
      <Route
        path="/nakes/*"
        element={
          <ProtectedRoute allowedRoles={["nakes"]}>
            <NakesLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="dashboard" element={<NakesDashboard />} />
        
        {/* Jobs */}
        <Route path="jobs" element={<NakesJobs />} />
        <Route path="jobs/shared" element={<NakesSharedJobs />} />
        <Route path="jobs/:id" element={<NakesJobDetail />} />
        <Route path="jobs/:id/workflow" element={<WorkflowJobDetail />} />
        <Route path="jobs/:id/stock-usage" element={<StockUsage />} />
        <Route path="jobs/:id/service-confirmation" element={<ServiceConfirmation />} />
        <Route path="jobs/:id/upload-ic" element={<UploadIC />} />
        <Route path="jobs/:id/upload-selfie" element={<UploadSelfie />} />
        <Route path="jobs/:id/upload-sampah" element={<UploadSampah />} />
        <Route path="jobs/:id/upload-dokumentasi" element={<UploadDokumentasi />} />
        
        {/* Schedule */}
        <Route path="schedule" element={<NakesSchedule />} />
        
        {/* Legacy tasks route - redirect to dashboard */}
        <Route path="tasks" element={<NakesDashboard />} />
        
        {/* Barang */}
        <Route path="my-usage" element={<MyUsageSummary />} />
        <Route path="supply-requests" element={<SupplyRequests />} />
        <Route path="supply-requests/:requestId" element={<NakesSupplyRequestDetail />} />
        <Route path="supply-history" element={<SupplyHistory />} />
        <Route path="logistic-returns" element={<NakesLogisticReturns />} />
        <Route path="logistic-returns/create" element={<NakesCreateLogisticReturn />} />
        <Route path="logistic-returns/:returnId" element={<NakesLogisticReturnDetail />} />
        <Route path="stok-opname" element={<NakesStokOpnamePage />} />
        {/* Rekap Gaji nakes disembunyikan dulu (Nov 2026). Untuk menyalakan lagi: import lazy pages/nakes/NakesRekapGaji lalu ganti Navigate dengan <NakesRekapGaji />. */}
        <Route path="rekap-gaji" element={<Navigate to="/nakes/dashboard" replace />} />
        <Route path="attendance" element={<NakesAttendancePage />} />
        
        {/* Other */}
        <Route path="assignments" element={<NakesAssignments />} />
        <Route path="privilege-history" element={<PrivilegeHistory />} />
        <Route path="chats" element={<NakesChats />} />
        <Route path="chats/:chatId" element={<NakesChatRoom />} />
        <Route path="job-discussions" element={<NakesJobDiscussions />} />
        <Route path="job-discussions/:orderId/:nakesId" element={<JobDiscussionRoom />} />
        <Route path="job-discussions/:orderId" element={<JobDiscussionRoom />} />
        <Route path="notifications" element={<NotificationsPage />} />
        
        {/* P0 - Scorecard */}
        <Route path="scorecard" element={<NakesScorecard />} />
        
        {/* P1 - Warning Letters */}
        <Route path="warning-letters" element={<NakesWarningLetters />} />
        
        {/* Pusat Info */}
        <Route path="announcements" element={<NakesAnnouncements />} />
        <Route path="announcements/:announcementId" element={<NakesAnnouncementDetail />} />
        <Route path="sop" element={<NakesSOP />} />
        <Route path="sop/:sopId" element={<NakesSOPDetail />} />
        <Route path="service-sops" element={<ServiceSOPListPage />} />
        <Route path="service-sops/:sopId" element={<ServiceSOPDetail />} />
        <Route path="knowledge-center/service-sops/:sopId" element={<ServiceSOPDetail />} />
        {/* Panduan & Aturan (iter376) — Nakes locked to own employment_type */}
        <Route path="guidelines" element={<NakesGuidelinesPage />} />
        <Route path="guidelines/:guidelineId" element={<NakesGuidelineDetail />} />
        {/* Tutorial nakes disembunyikan (Nov 2026). Untuk menyalakan lagi: ganti Navigate dengan <TutorialPage />. */}
        <Route path="tutorial" element={<Navigate to="/nakes/bantuan" replace />} />
        <Route path="apa-yang-baru" element={<AppUpdatesPage />} />
        <Route path="bantuan" element={<HelpCenterPage />} />
        
        <Route path="profile" element={<NakesProfile />} />
        <Route path="wilayah-layanan" element={<WilayahLayanan />} />

        {/* Stock Monitor — DISABLED for Nakes role (Feb 2026): blocked + redirect */}
        <Route path="stock-monitor" element={<NakesStockMonitorBlocked />} />
        {/* Kartu Stok Saya (halaman nakes) DISEMBUNYIKAN dulu (Okt 2026). Untuk menyalakan lagi: import pages/nakes/KartuStokSaya dan route kartu-stok. */}
      </Route>

      {/* Shared PDF Viewer Route - accessible by all roles */}
      <Route path="/pdf-viewer" element={<PDFViewer />} />
      <Route path="/invoice-viewer" element={<InvoiceViewer />} />
      <Route path="/po-viewer" element={<POPDFViewer />} />
      <Route path="/surat-tugas-viewer" element={<SuratTugasViewer />} />
      <Route path="/surat-pengantar-viewer" element={<SuratPengantarViewer />} />
      <Route path="/surat-sakit-viewer" element={<SuratSakitViewer />} />
      
      {/* Public Routes - Teleconsultation */}
      <Route path="/consult/:link" element={<PatientConsultation />} />

      {/* Public Referral Dashboard - No login required, token auth */}
      <Route path="/ref/dashboard/:token" element={<ReferralDashboard />} />
      
      {/* Public Vaccine Record - No login required, token auth */}
      <Route path="/public/vaccine-record" element={<PublicVaccineRecord />} />

      {/* Public Screening Form - No login required, token auth */}
      <Route path="/public/screening/:token" element={<ScreeningForm />} />

      {/* Public Order Screening Share - No login required, read-only, share_token auth */}
      <Route path="/public/order-screening/:share_token" element={<PublicOrderScreening />} />

      {/* Public Booking Form via Link - No login required, per-marketing persistent token */}
      <Route path="/public/booking/:token" element={<PublicBookingForm />} />

      {/* Public Patient Summary - No login required, token auth */}
      <Route path="/summary/:shareToken" element={<PublicSummary />} />

      {/* Public Customer Payment Page (Midtrans) — no auth required */}
      <Route path="/pay/:invoiceId" element={<CustomerPaymentPage />} />
      <Route path="/pay/:invoiceId/result" element={<CustomerPaymentResultPage />} />
      <Route path="/receipt/:invoiceId/public" element={<ReceiptPublicPage />} />
      
      {/* ARCHITECTURE FIX: Redirect /super_admin/* and /master_admin/* to /admin/* */}
      {/* Super Admin & Master Admin use SAME routes as Admin - no separate namespace */}
      <Route path="/super_admin/*" element={
        <Navigate to={window.location.pathname.replace('/super_admin', '/admin') + window.location.search} replace />
      } />
      <Route path="/master_admin/*" element={
        <Navigate to={window.location.pathname.replace('/master_admin', '/admin') + window.location.search} replace />
      } />

      {/* Gudang Routes */}
      <Route
        path="/gudang/*"
        element={
          <ProtectedRoute allowedRoles={["gudang"]}>
            <GudangLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - Command Center */}
        <Route path="dashboard" element={<GudangDashboard />} />
        <Route path="home" element={<GudangDashboard />} />
        
        {/* Pengiriman Barang (renamed from Supply Requests) */}
        <Route path="pengiriman" element={<GudangSupplyRequests />} />
        <Route path="pengiriman/:requestId" element={<GudangSupplyRequestDetail />} />
        {/* Keep old routes for backward compatibility */}
        <Route path="supply-requests" element={<GudangSupplyRequests />} />
        <Route path="supply-requests/:requestId" element={<GudangSupplyRequestDetail />} />
        
        {/* Retur Barang (renamed from Logistic Returns) */}
        <Route path="retur" element={<GudangLogisticReturns />} />
        <Route path="retur/create" element={<GudangCreateLogisticReturn />} />
        <Route path="retur/:returnId" element={<GudangLogisticReturnDetail />} />
        {/* Keep old routes for backward compatibility */}
        <Route path="logistic-returns" element={<GudangLogisticReturns />} />
        <Route path="logistic-returns/create" element={<GudangCreateLogisticReturn />} />
        <Route path="logistic-returns/:returnId" element={<GudangLogisticReturnDetail />} />
        
        {/* Okt 2026 - Komplain Nakes dari Gudang */}
        <Route path="komplain-nakes" element={<GudangKomplainNakes />} />

        {/* Chat - With Filters */}
        <Route path="chat" element={<GudangChat />} />
        <Route path="chat-nakes" element={<ChatNakes />} />
        <Route path="chat-nakes/:nakesId" element={<ChatNakesDetail />} />
        <Route path="coordination-channels" element={<CoordinationChannels />} />
        <Route path="coordination-channels/:channelType" element={<CoordinationChannels />} />
        
        {/* Pemesanan Barang */}
        <Route path="purchase-orders" element={<GudangPurchaseOrders />} />
        <Route path="purchase-orders/:poId" element={<GudangPurchaseOrderDetail />} />
        <Route path="master-supplier" element={<GudangMasterSupplier />} />
        <Route path="master-vaxa-product" element={<GudangMasterVaxaProduct />} />
        <Route path="konfirmasi-produk" element={<ProductConfirmations />} />
        <Route path="master-supplier-product" element={<GudangMasterSupplierProduct />} />

        {/* Stock Monitor — Gudang Pusat */}
        <Route path="stock-monitor/gudang" element={<StockMonitorGudang />} />

        {/* Stock Monitor — Nakes */}
        <Route path="stock-monitor/nakes" element={<StockMonitorNakes />} />
        <Route path="kartu-stok" element={<KartuStok />} />
        {/* Alamat lama tetap jalan */}
        <Route path="kartu-stok-nakes" element={<Navigate to="/gudang/kartu-stok" replace />} />
        <Route path="kartu-stok-gudang" element={<Navigate to="/gudang/kartu-stok" replace />} />
        
        {/* Pusat Informasi */}
        <Route path="announcements" element={<GudangAnnouncements />} />
        <Route path="announcements/:announcementId" element={<GudangAnnouncementDetail />} />
        <Route path="service-sops" element={<ServiceSOPListPage />} />
        <Route path="service-sops/:sopId" element={<ServiceSOPDetail />} />
        <Route path="tutorial" element={<TutorialPage />} />
        <Route path="apa-yang-baru" element={<AppUpdatesPage />} />
        
        {/* Stok Opname Nakes */}
        <Route path="stok-opname" element={<AdminStokOpnameList />} />
        <Route path="stok-opname/:id" element={<AdminStokOpnameDetail />} />

        {/* Other */}
        <Route path="nakes-history/:nakesId" element={<NakesHistoryGudang />} />
        <Route path="notifications" element={<GudangNotifications />} />
        <Route path="profile" element={<GudangProfile />} />
        
        {/* Papan Operasional — shared */}
        <Route path="papan-operasional" element={<PapanOperasional />} />
        <Route path="papan-operasional/:poId" element={<PapanOperasionalThread />} />
      </Route>

      {/* Finance Routes */}
      <Route
        path="/finance/*"
        element={
          <ProtectedRoute allowedRoles={["finance", "admin"]}>
            <RequirePermission permission="finance.console" bypassRoles={["finance"]}><FinanceLayout /></RequirePermission>
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="dashboard" element={<FinanceDashboard />} />
        <Route index element={<FinanceDashboard />} />
        
        {/* Invoice Management — INVOICE / KEDUANYA only */}
        {/* Feb 2026 — Manajemen Order READ-ONLY untuk finance.
            Reuse admin OrdersList + OrderDetail; kedua halaman men-detect
            path prefix '/finance/' via useLocation dan hide action buttons. */}
        <Route path="orders" element={<AdminOrdersList />} />
        <Route path="orders/:orderId" element={<OrderDetail />} />
        {/* Okt 2026 — Finance boleh melihat Workflow Nakes (baca saja) */}
        <Route path="orders/:orderId/workflow" element={<WorkflowDetail />} />

        <Route path="invoices" element={<RequireFinanceScope scope="INVOICE"><Invoices /></RequireFinanceScope>} />
        <Route path="invoices/no-proof" element={<RequireFinanceScope scope="INVOICE"><InvoicesNoProof /></RequireFinanceScope>} />
        {/* Okt 2026 — Pembayaran Midtrans (baca saja) */}
        <Route path="midtrans-payments" element={<RequireFinanceScope scope="INVOICE"><MidtransPayments /></RequireFinanceScope>} />
        <Route path="invoices/new/:orderId" element={<RequireFinanceScope scope="INVOICE"><InvoiceForm /></RequireFinanceScope>} />
        <Route path="invoices/:invoiceId" element={<RequireFinanceScope scope="INVOICE"><InvoiceDetail /></RequireFinanceScope>} />
        <Route path="invoices/:invoiceId/edit" element={<RequireFinanceScope scope="INVOICE"><InvoiceForm /></RequireFinanceScope>} />

        {/* Customer 360 — INVOICE / KEDUANYA only (Feb 2026: granted for FIN002 access) */}
        <Route path="customers/360/:customerId" element={<RequireFinanceScope scope="INVOICE"><Customer360 /></RequireFinanceScope>} />
        <Route path="customers/360/:customerId/riwayat-layanan" element={<RequireFinanceScope scope="INVOICE"><CustomerServiceHistory /></RequireFinanceScope>} />
        
        {/* Purchase Orders (Supplier Payment) — SUPPLIER / KEDUANYA only */}
        <Route path="purchase-orders" element={<RequireFinanceScope scope="SUPPLIER"><PurchaseOrders /></RequireFinanceScope>} />
        <Route path="purchase-orders/:poId" element={<RequireFinanceScope scope="SUPPLIER"><PurchaseOrderDetail /></RequireFinanceScope>} />
        
        {/* Chat - Internal */}
        <Route path="chats/admin" element={<CoordinationChannels />} />
        <Route path="chats/gudang" element={<CoordinationChannels />} />
        <Route path="chats/marketing" element={<FinanceChatMarketing />} />
        <Route path="chats/marketing/:userId" element={<FinanceChatMarketingThread />} />
        <Route path="coordination-channels" element={<CoordinationChannels />} />
        <Route path="coordination-channels/:channelType" element={<CoordinationChannels />} />
        
        {/* Pusat Informasi */}
        <Route path="announcements" element={<KnowledgeCenter />} />
        <Route path="announcements/:announcementId" element={<AnnouncementDetail />} />
        <Route path="knowledge-center/service-sops/:sopId" element={<ServiceSOPDetail />} />
        <Route path="tutorial" element={<TutorialPage />} />
        <Route path="apa-yang-baru" element={<AppUpdatesPage />} />
        
        {/* Other */}
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="profile" element={<AdminProfile />} />
        
        {/* Papan Operasional — shared */}
        <Route path="papan-operasional" element={<PapanOperasional />} />
        <Route path="papan-operasional/:poId" element={<PapanOperasionalThread />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <BrowserRouter>
          <AppRoutes />
          <ChatToastNotifier />
          <ConnectivityLayer />
          {/* Fix 22 — Compact top-right toaster that doesn't cover the mobile
              hamburger. `offset` bumps it below the app header (48px), and
              width caps prevent full-width takeover on small screens. */}
          {/* Fix 30B — Toast global config: center-horizontal, max 320px width,
              margin kiri-kanan sama. Sebelumnya `top-right` yang di mobile
              tampil dari kanan tapi bisa menutupi UI karena tidak center.
              `top-center` + inline styles memusatkan di viewport dan lebar
              tidak melebihi 320px. */}
          <Toaster
            position="top-center"
            richColors
            closeButton
            duration={2000}
            offset={56}
            gap={8}
            toastOptions={{
              className: "text-sm mx-auto",
              style: {
                width: "min(320px, calc(100vw - 32px))",
                maxWidth: "320px",
                marginLeft: "auto",
                marginRight: "auto",
                padding: "8px 12px",
                fontSize: "13px",
              },
            }}
          />
          <PdfViewerModal />
        </BrowserRouter>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;
